import { Doc, TableNames } from "../../_generated/dataModel";
import { FilterRule } from "../types/filters_types";

export type ExtractFieldPaths<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ? K | `${K}.${ExtractFieldPaths<T[K]>}`
          : K
        : never;
    }[keyof T]
  : never;

export type TableScope = "company" | "branch";

export interface RelationConfig {
  key: string;
  schema: Record<string, any>;
  tableName: string;
  many?: boolean;
  sourceField?: string;
  /** Campos de la tabla relacionada en los que se puede buscar */
  searchableFields?: ReadonlyArray<string>;
}

export interface DomainTableConfig<T extends TableNames = TableNames> {
  tableName: T;
  schema: Record<string, any>;
  scope?: TableScope;
  /**
   * Si true, la tabla es dato maestro (clientes, productos, servicios) y su
   * scope se resuelve en runtime leyendo `masterDataScope` de la empresa del
   * usuario: "branch" filtra por branchId, "company" no filtra. Tiene prioridad
   * sobre `scope` cuando masterDataScope === "branch".
   */
  masterData?: boolean;
  relations?: RelationConfig[];
  searchFields?: ReadonlyArray<string>;
  hasStatusIndex?: boolean;
  customRules?: FilterRule<Doc<T>>[];
  searchIndex?: { name: string; field: string };
  /**
   * Si true, las queries paginadas agregan `tags` (etiquetas de entity_tags,
   * con entityType = tableName) a cada fila de la página, como una relación.
   * Ver table_search/enrich_tags.ts.
   */
  withTags?: boolean;
  /**
   * Acciones de escritura que los scripts pueden pedir con
   * `events.emit(tabla, accion, payload)`. Opt-in: sin declarar, los scripts
   * solo leen la tabla. Se procesan con `SecureTable` y los permisos del
   * autor del script (ver `convex/companies/scripts/emitted.ts`). No
   * declarar en tablas con logica de negocio propia (facturas, cotizaciones):
   * esas van como acciones de dominio.
   */
  scriptActions?: ReadonlyArray<import("../scripting/script_actions").ScriptActionName>;
  /**
   * Aliases que el sandbox aplica en `where({...})` y `orderBy({...})` de
   * `tables.X.findMany` para tablas que exponen el dato bajo otro nombre
   * (ej: `clients` no tiene campo `status` propio; el estado vive en
   * `internalControl.state`). El admin escribe `where({ status: "active" })`
   * y el sandbox traduce a `where({ "internalControl.state": "active" })`
   * usando este mapa. Solo aplica a tablas SIN el campo propio (si la tabla
   * ya tiene `status` como campo de tabla, el alias se ignora: el campo
   * real gana). Ver `lib/shared/function_field_tables.ts`.
   */
  scriptFieldAliases?: Readonly<Record<string, string>>;
  /**
   * Expone `globals.<modulo>` a los scripts: valores compartidos por la
   * empresa (tabla `company_globals`), con los permisos del modulo (leer =
   * canRead, escribir = canUpdate). Opt-in. Ver
   * `lib/shared/function_field_globals.ts`.
   */
  scriptGlobals?: boolean;
}

export type LucideIconName = string;
export type Permission = "canCreate" | "canRead" | "canUpdate" | "canDelete";

/**
 * Nivel de acceso por defecto de un rol sobre un módulo. Se intersecta con el
 * techo `permissions` del módulo (full nunca otorga una acción que el módulo no
 * declara).
 *  - full:  todas las acciones declaradas por el módulo (R/C/U/D)
 *  - write: read + create + update (sin delete)
 *  - read:  solo lectura
 *  - none:  sin acceso (el módulo no se incluye para ese rol)
 */
export type AccessLevel = "full" | "write" | "read" | "none";

/**
 * Roles del sistema, para declarar defaults de permisos en la config de dominio.
 * Espejo de `roleType` (definido sin import para evitar ciclos con
 * convex/system/roles).
 */
export type RoleTypeId = "super_admin" | "company_admin" | "company_user";

/**
 * Acceso por defecto por rol. Declarable a nivel grupo y/o módulo. El valor es
 * un nivel preset (`full|write|read|none`) O una lista explícita de acciones
 * para combinaciones arbitrarias, ej. `["canRead", "canCreate"]` (read + create,
 * sin update/delete). Siempre se intersecta con el techo `permissions` del módulo.
 */
export type DefaultAccessByRole = Partial<
  Record<RoleTypeId, AccessLevel | Permission[]>
>;

/**
 * Estado de disponibilidad de un módulo
 */
export type ModuleStatus = "active" | "coming-soon" | "deprecated" | "inactive";
export interface ReadOnlyTableRef {
  tableName: TableNames;
}
/**
 * Nivel de acceso requerido para mostrar widgets de este modulo en el dashboard.
 * Combina tipo de rol + permisos para decidir visibilidad.
 *
 * - "standard":   Cualquier usuario con canRead (comportamiento por defecto)
 * - "elevated":   Solo company_admin/super_admin O usuarios con canCreate/canUpdate
 * - "admin_only": Solo company_admin y super_admin
 * - "none":       Nunca visible en dashboard
 */
export type DashboardAccessLevel =
  | "standard"
  | "elevated"
  | "admin_only"
  | "none";

/**
 * Contextos donde puede aparecer un módulo en navegación
 */
export type NavigationContext =
  | "main" // Navegación principal (sidebar)
  | "settings" // Menú de configuración
  | "super" // Panel de super admin
  | "billing" // Navegación de facturación
  | "mobile" // Barra inferior móvil
  | "none"; // No aparece en navegación

export interface NavigationMetadata {
  /** Si debe mostrarse en navegación */
  showInNav: boolean;
  /** Contextos donde aparece */
  contexts: NavigationContext[];
  /** Orden de visualización en navegación (menor = primero) */
  navOrder: number | Partial<Record<NavigationContext, number>>;
  /** Estado del módulo */
  status: ModuleStatus;
}

// ============================================================================
// I18N TYPES
// ============================================================================

/**
 * Keys de acciones comunes traducibles
 */
export interface ModuleI18nActionKeys {
  create?: string;
  edit?: string;
  delete?: string;
  export?: string;
  import?: string;
  view?: string;
  duplicate?: string;
  archive?: string;
  restore?: string;
}

/**
 * Configuración de campos de enum traducibles
 */
export interface TranslatableEnumConfig {
  /** Nombre del campo en el schema */
  field: string;
  /** Valores posibles del enum */
  values: readonly string[];
}

/**
 * Keys de i18n para un módulo
 */
export interface ModuleI18nKeys {
  /** Título del módulo */
  title: string;
  /** Descripción corta */
  description: string;
  /** Descripción larga (para tooltips/ayuda) */
  longDescription?: string;
  /** Placeholder de búsqueda */
  searchPlaceholder?: string;
  /** Texto cuando no hay registros */
  emptyState?: string;
  /** Texto de error genérico */
  errorState?: string;
  /** Keys de acciones */
  actions?: ModuleI18nActionKeys;
}

/**
 * Configuración completa de i18n para un módulo
 */
export interface ModuleI18nConfig {
  /** Namespace base (ej: "logistics.orders") */
  namespace: string;
  /** Keys de traducción */
  keys: ModuleI18nKeys;
  /** Campos de la tabla que soportan contenido multi-idioma */
  translatableFields?: readonly string[];
  /** Enums con valores que necesitan traducción */
  translatableEnums?: readonly TranslatableEnumConfig[];
}

/**
 * Keys de i18n para un dominio
 */
export interface DomainI18nKeys {
  /** Título del dominio */
  title: string;
  /** Descripción del dominio */
  description: string;
}

/**
 * Configuración de i18n para un dominio
 */
export interface DomainI18nConfig {
  /** Namespace del dominio (ej: "logistics") */
  namespace: string;
  /** Keys de traducción */
  keys: DomainI18nKeys;
}

// ============================================================================
// MODULE & DOMAIN INTERFACES
// ============================================================================

export interface DomainModule {
  // De ERP_MODULES_CONFIG
  id: string;
  name: string;
  description: string;
  icon?: LucideIconName;
  permissions: Permission[];
  /**
   * Acceso por defecto por rol para este módulo. Si se omite un rol, hereda el
   * del grupo y, en su defecto, el default global (ver default_permissions.ts).
   * Override de lo declarado a nivel grupo.
   */
  defaultAccess?: DefaultAccessByRole;
  routes: string[];
  navigation?: NavigationMetadata;
  // Tablas que controla este módulo (ya no son strings sueltos)
  tables: DomainTableConfig[];
  readOnlyTables: ReadOnlyTableRef[];
  /** Nivel de acceso para widgets del dashboard (default: "standard") */
  dashboardAccess?: DashboardAccessLevel;
  /** Configuración de internacionalización */
  i18n?: ModuleI18nConfig;
}

/**
 * Edición de distribución en la que se compila un dominio.
 * - "saas":    versión hospedada en la nube (backend de Convex propio)
 * - "desktop": app Tauri que apunta al backend en la nube
 * - "onprem":  versión self-host del cliente (backend local, SIN joyas)
 *
 * Un dominio sin `editions` se considera disponible en TODAS las ediciones
 * (Tier 0/1 — infraestructura y core commodity). Los dominios "joya"
 * (Tier 2) omiten "onprem" para que su código NO entre al bundle on-prem.
 */
export type Edition = "saas" | "desktop" | "onprem" | "free_desktop";

export const ALL_EDITIONS: readonly Edition[] = [
  "saas",
  "desktop",
  "onprem",
  "free_desktop",
];

/**
 * Ediciones de instalación local self-host (backend propio en la máquina del
 * cliente). Comparten comportamiento: single-tenant, gate de licencia,
 * onboarding que aprovisiona desde el token, sin suscripción cloud.
 *  - "onprem":       distribución empresarial (Docker), token de pago.
 *  - "free_desktop": instalador gratis sin Docker (backend embebido), token free.
 * Ambas EXCLUYEN las joyas (igual filtrado de dominios: un dominio joya lista
 * `editions: ["saas","desktop"]`, dejando fuera a onprem y free_desktop).
 */
export const LOCAL_INSTALL_EDITIONS: readonly Edition[] = [
  "onprem",
  "free_desktop",
];

export interface DomainGroup {
  id: string;
  name: string;
  description: string;
  icon: LucideIconName;
  order: number;
  status?: ModuleStatus;
  /**
   * Solo NAVEGACIÓN: agrupa los módulos de este dominio bajo OTRO grupo en el
   * sidebar/tabs (por id de grupo), sin mover permisos, tablas ni gating de
   * edición (esos siguen usando el grupo real). Ej: notas de crédito/débito
   * viven en su propio dominio pero se muestran bajo "Facturación".
   */
  navParentGroup?: string;
  /**
   * Acceso por defecto por rol para todos los módulos del grupo. Cada módulo
   * puede overridear con su propio `defaultAccess`.
   */
  defaultAccess?: DefaultAccessByRole;
  modules: DomainModule[];
  /** Configuración de internacionalización */
  i18n?: DomainI18nConfig;
  /**
   * Ediciones en las que este dominio se compila. Si se omite, está en todas.
   * Las joyas (Tier 2) deben excluir "onprem". Ver docs/editions-architecture.md
   */
  editions?: readonly Edition[];
}
